import * as yup from 'yup';
import { Formik } from 'formik';
import React, { useRef } from 'react';
import { gql, useMutation } from '@apollo/client';

import { RetailCommissionForm } from '../../../views/retailCommission/components';
import { CreateUpdateModal } from '../../../components/modals';
import { useFormOptions } from '../../../hooks/forms';

import { usePolicyPricingOptions } from '../../../components/sharedComponents/pricing';
import { convertDecimalToPercentage, convertPercentageToDecimal } from '../../../utils';
import { useRetailCommissionRateModes } from '../../retailCommission/hooks';

import { GET_POLICY_PRICINGS } from '../../policyPricing/hooks/usePolicyPricings';

const CREATE_POLICY_RETAIL_COMMISSION = gql`
  mutation CreatePolicyRetailCommission($input: CreatePolicyRetailCommissionInput!) {
    createPolicyRetailCommission(input: $input) {
      id
      retailCommissions {
        id
        name
        rateMode
        rate
        appliesToAllPricing
        pricingOptionIds
      }
    }
  }
`;

const UPDATE_POLICY_RETAIL_COMMISSION = gql`
  mutation EditPolicyRetailCommission($input: EditPolicyRetailCommissionInput!) {
    editPolicyRetailCommission(input: $input) {
      id
      retailCommissions {
        id
        name
        rateMode
        rate
        appliesToAllPricing
        pricingOptionIds
      }
    }
  }
`;

export default function CreateUpdate({ isOpen, toggle, policyId, retailCommission, onError = () => {} }) {
  const [create, { loading: creating, error: createError }] = useMutation(CREATE_POLICY_RETAIL_COMMISSION, {
    onCompleted: toggle,
    onError,
    refetchQueries: [{ query: GET_POLICY_PRICINGS, variables: { id: policyId } }]
  });

  const [edit, { loading: updating, error: updateError }] = useMutation(UPDATE_POLICY_RETAIL_COMMISSION, {
    onCompleted: toggle,
    onError,
    refetchQueries: [{ query: GET_POLICY_PRICINGS, variables: { id: policyId } }]
  });

  const isCreateMode = !retailCommission;
  const focusRef = useRef(null);

  const commissionAppliesToOptions = [
    {
      label: 'All Pricing Options',
      value: true
    },
    {
      label: 'Selected Pricing Options',
      value: false
    }
  ];

  const { options: rateModeOptions, loading: loadingRateModeOptions } = useRetailCommissionRateModes();
  const { selectOptions: policyPricingOptions, loading: loadingPolicyPricingOptions } = usePolicyPricingOptions({
    policyId
  });

  const formOptions = useFormOptions({
    options: [
      { name: 'rateMode', options: rateModeOptions, loading: loadingRateModeOptions },
      { name: 'pricing', options: policyPricingOptions, loading: loadingPolicyPricingOptions },
      { name: 'commissionAppliesTo', options: commissionAppliesToOptions }
    ]
  });

  const initialValues = {
    name: retailCommission?.name || '',
    rate: !!retailCommission?.rate ? convertDecimalToPercentage(retailCommission?.rate) : '',
    rateMode: rateModeOptions?.find(({ value }) => value === 'PERCENTAGE'),
    appliesToAllPricing:
      commissionAppliesToOptions?.find(option => option.value === retailCommission?.appliesToAllPricing) ||
      commissionAppliesToOptions[1],
    pricings: policyPricingOptions?.filter(option => retailCommission?.pricingOptionIds.includes(option.value)) || []
  };

  const handleSubmit = values => {
    const input = {
      name: values?.name,
      rate: convertPercentageToDecimal(values?.rate),
      rateMode: values?.rateMode?.value,
      appliesToAllPolicyPricing: values?.appliesToAllPricing?.value,
      policyPricingIds: values?.pricings?.map(pricing => pricing?.value)
    };

    if (isCreateMode) {
      create({
        variables: { input: { policyId, ...input } }
      });
    } else {
      edit({
        variables: {
          input: { retailCommissionId: retailCommission?.id, ...input }
        }
      });
    }
  };

  const validationSchema = yup.object({
    name: yup.string().required('Required'),
    rate: yup.string().required('Required'),
    rateMode: yup.object({
      value: yup.string().required('Required'),
      label: yup.string().required('Required')
    }),
    appliesToAllPricing: yup.object({ label: yup.string(), value: yup.boolean() }).required('Required'),
    pricings: yup.array().when('appliesToAllPricing', {
      is: val => val?.value === true,
      then: schema => schema.nullable(),
      otherwise: schema => schema.of(yup.object({ label: yup.string(), value: yup.string() })).required('Required')
    })
  });

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {formik => (
        <CreateUpdateModal
          isOpen={isOpen}
          toggle={toggle}
          entityName="Retail Commission"
          creating={creating}
          updating={updating}
          isCreateMode={isCreateMode}
          onSubmit={formik.handleSubmit}
          onOpened={() => {
            focusRef?.current?.focus();
            formik.handleReset();
          }}
        >
          <RetailCommissionForm
            formik={formik}
            error={createError || updateError}
            focusRef={focusRef}
            formOptions={formOptions}
          />
        </CreateUpdateModal>
      )}
    </Formik>
  );
}
